// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useEffect, useState } from 'react';
import { Spinner } from '@loa/ui-components';
import { useBankConnectionStatus } from '../../hooks/useBankConnectionStatus';
import openBankingApi from '../../api/openBankingApi';
import { BankAccountCard } from '../openbanking/BankAccountCard';
import { BankStatisticsChart } from '../openbanking/BankStatisticsChart';
import { BankTransactionsTable } from '../openbanking/BankTransactionsTable';
import type { BankAccount } from '../../types/openBanking';
import './ClientLinkedBankAccountsTab.css';

interface ClientLinkedBankAccountsTabProps {
  /**
   * The client's federated (Keycloak) user id — sourced from
   * {@code ClientDetailDTO.federatedUserId}. Null/undefined when the
   * client has been invited but never logged in.
   */
  federatedUserId?: string;
  /** Display name used in the empty / failure state copy. */
  clientName: string;
}

/**
 * Linked Bank Accounts — the advisor's read-only view of the client's
 * connected bank accounts, sourced from open-banking-server through
 * advisor-bff's {@code /api/openbanking/**} proxy.
 *
 * <p>Lifecycle handling mirrors the Client UI's Bank Info page:
 * Refreshing/Loading* → spinner with progress copy; *Failed → error
 * card; LoadingCompleted → accounts list with drill-down chart and
 * transactions per selected account.
 *
 * <p>V1 authorisation is brokerage-scope only: any advisor in the
 * brokerage can view any client's bank data. Tightening to
 * advisor-manages-this-client is a documented follow-up.
 */
export function ClientLinkedBankAccountsTab({
  federatedUserId,
  clientName,
}: ClientLinkedBankAccountsTabProps) {
  const {
    status, loading: statusLoading, isPolling, isCompleted, isFailed, noProvider,
    error: statusError,
  } = useBankConnectionStatus(federatedUserId);

  const [accounts, setAccounts] = useState<BankAccount[] | null>(null);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [accountsError, setAccountsError] = useState<Error | null>(null);
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);

  useEffect(() => {
    if (!federatedUserId || !isCompleted || noProvider) return;
    let cancelled = false;
    setAccountsLoading(true);
    setAccountsError(null);
    openBankingApi.getAccounts(federatedUserId)
      .then((res) => {
        if (cancelled) return;
        setAccounts(res);
        if (res.length > 0 && !selectedAccountId) {
          setSelectedAccountId(res[0].id);
        }
      })
      .catch((e) => {
        if (cancelled) return;
        setAccountsError(e instanceof Error ? e : new Error(String(e)));
      })
      .finally(() => {
        if (!cancelled) setAccountsLoading(false);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [federatedUserId, isCompleted, noProvider]);

  if (!federatedUserId) {
    return <EmptyState clientName={clientName} />;
  }
  if (statusError) {
    return (
      <div className="client-linked-bank-accounts__error">
        Couldn't load bank info: {statusError.message}
      </div>
    );
  }
  if (statusLoading) {
    return <LoadingState message="Loading bank info..." />;
  }
  if (noProvider) {
    return <EmptyState clientName={clientName} />;
  }
  if (isPolling) {
    return <LoadingState message={inProgressMessage(status?.accountStatus)} />;
  }
  if (isFailed) {
    return <FailureState clientName={clientName} statusName={status?.accountStatus} />;
  }
  if (accountsError) {
    return (
      <div className="client-linked-bank-accounts__error">
        Couldn't load accounts: {accountsError.message}
      </div>
    );
  }
  if (accountsLoading && !accounts) {
    return <LoadingState message="Loading bank accounts..." />;
  }
  if (!accounts || accounts.length === 0) {
    return <EmptyState clientName={clientName} />;
  }

  return (
    <div className="client-linked-bank-accounts">
      {accounts.map((account) => {
        const expanded = account.id === selectedAccountId;
        return (
          <BankAccountCard
            key={account.id}
            account={account}
            selected={expanded}
            onClick={() => setSelectedAccountId(expanded ? null : account.id)}
          >
            {expanded ? (
              <div className="client-linked-bank-accounts__drilldown">
                <BankStatisticsChart
                  userId={federatedUserId}
                  accountId={account.id}
                  currencyCode={account.currencyCode}
                />
                <BankTransactionsTable
                  userId={federatedUserId}
                  accountId={account.id}
                  currencyCode={account.currencyCode}
                />
              </div>
            ) : null}
          </BankAccountCard>
        );
      })}
    </div>
  );
}

function LoadingState({ message }: { message: string }) {
  return (
    <div className="client-linked-bank-accounts__loading">
      <Spinner text={message} />
    </div>
  );
}

function EmptyState({ clientName }: { clientName: string }) {
  return (
    <div className="client-linked-bank-accounts__empty">
      <h3 className="client-linked-bank-accounts__empty-title">No bank connected yet</h3>
      <p className="client-linked-bank-accounts__empty-text">
        {clientName} hasn't connected a bank account. Send them an
        Open Banking link request from the inbox to begin.
      </p>
    </div>
  );
}

function FailureState({ clientName, statusName }: { clientName: string; statusName?: string }) {
  return (
    <div className="client-linked-bank-accounts__empty">
      <h3 className="client-linked-bank-accounts__empty-title">Couldn't load bank data</h3>
      <p className="client-linked-bank-accounts__empty-text">
        We weren't able to {failureCopy(statusName)} for {clientName}. Ask
        them to re-link their bank from the inbox.
      </p>
    </div>
  );
}

function inProgressMessage(status?: string): string {
  switch (status) {
    case 'Refreshing': return 'Refreshing bank data...';
    case 'LoadingAccounts': return 'Loading accounts...';
    case 'LoadingTransactions': return 'Loading transactions...';
    case 'LoadingAnalytics': return 'Calculating statistics...';
    default: return 'Loading bank info...';
  }
}

function failureCopy(status?: string): string {
  switch (status) {
    case 'LoadingAccountsFailed': return 'load the account list';
    case 'LoadingTransactionsFailed': return 'load transactions';
    case 'LoadingAnalyticsFailed': return 'compute statistics';
    default: return 'load bank data';
  }
}
